import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";
import * as stylex from "@stylexjs/stylex";
import { cn } from "@/lib/utils/index";
const buttonVariants = cva("focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-xl border border-transparent bg-clip-padding text-sm font-semibold focus-visible:ring-3 aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center justify-center whitespace-nowrap transition-all duration-200 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none shrink-0 [&_svg]:shrink-0 outline-none group/button select-none", {
  variants: {
    variant: {
      default: "border-primary/10 bg-linear-to-r from-primary via-[color-mix(in_oklch,var(--primary)_84%,white)] to-[color-mix(in_oklch,var(--accent)_32%,var(--primary))] text-primary-foreground shadow-[0_18px_35px_-24px_color-mix(in_oklch,var(--primary)_90%,transparent)] hover:-translate-y-0.5 hover:brightness-105",
      outline: "border-primary/12 bg-card/80 text-foreground shadow-[0_12px_26px_-24px_color-mix(in_oklch,var(--foreground)_70%,transparent)] hover:-translate-y-0.5 hover:bg-card dark:bg-card/80 dark:border-input dark:hover:bg-card aria-expanded:bg-muted aria-expanded:text-foreground",
      themed: "border-primary/12 bg-linear-to-br from-[color-mix(in_oklch,var(--secondary)_50%,var(--card))] to-[color-mix(in_oklch,var(--card)_88%,var(--accent))] text-foreground hover:-translate-y-0.5 hover:from-[color-mix(in_oklch,var(--secondary)_60%,var(--card))] hover:to-[color-mix(in_oklch,var(--card)_78%,var(--accent))] shadow-[0_20px_36px_-28px_color-mix(in_oklch,var(--primary)_70%,transparent)]",
      secondary: "border border-transparent bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary)_82%,white)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
      ghost: "text-foreground/78 hover:bg-card/72 hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground",
      destructive: "bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30",
      link: "text-primary underline-offset-4 hover:underline"
    },
    size: {
      default: "h-10 gap-1.5 px-4 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
      xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
      sm: "h-8 gap-1.5 rounded-[min(var(--radius-md),12px)] px-3 text-[0.82rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 [&_svg:not([class*='size-'])]:size-3.5",
      lg: "h-11 gap-2 px-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
      icon: "size-10",
      "icon-xs": "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
      "icon-sm": "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
      "icon-lg": "size-9"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
function Button({
  className,
  variant = "default",
  size = "default",
  stylexStyle,
  style,
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants> & {
  stylexStyle?: stylex.StyleXStyles;
}) {
  const stylexProps = stylex.props(stylexStyle);
  return <ButtonPrimitive data-slot="button" className={cn(buttonVariants({
    variant,
    size,
    className
  }), stylexProps.className)} style={typeof style === "function" ? state => ({
    ...stylexProps.style,
    ...style(state)
  }) : {
    ...stylexProps.style,
    ...style
  }} {...props} />;
}
export { Button, buttonVariants };